// src/scene/focus.ts
//
// Shared "follow-you" focus state. The camera is constrained (looks straight
// −X, climbs in Y, Z locked), so LIGHT is the expressive tool: a pool of
// gallery light tracks the viewer up the dark wall — works ignite as you reach
// them and fall back to shadow as you pass.
//
// This module owns a single set of THREE.IUniform objects that are SHARED (by
// reference) into every fog-aware wall ShaderMaterial and read by the
// atmosphere/god-ray code. `main.ts` updates `uFocusY` each frame from the
// eased camera Y (Controls.focusY). Because the uniform objects are shared,
// one assignment per frame lights the whole wall — no per-material loop.

import * as THREE from 'three';

/** Live, shared focus uniforms. Merge `FOCUS_UNIFORMS` into any wall material
 *  (by reference, NOT cloned) so a single per-frame write drives them all. */
export const FOCUS_UNIFORMS: {
  uFocusY: THREE.IUniform<number>;
  uFocusSpan: THREE.IUniform<number>;
  uFocusGain: THREE.IUniform<number>;
} = {
  /** Eased world-Y the viewer is centred on (the height of the light pool). */
  uFocusY: { value: 1.1 },
  /** Vertical half-width of the lit pool (world units of soft falloff). */
  uFocusSpan: { value: 3.4 },
  /** How hard the wall outside the pool is pushed toward shadow (0..1). */
  uFocusGain: { value: 1.0 },
};

/** Convenience getters/setters so callers don't reach into `.value`. */
export const focus = {
  setY(y: number): void { FOCUS_UNIFORMS.uFocusY.value = y; },
  getY(): number { return FOCUS_UNIFORMS.uFocusY.value; },
  get span(): number { return FOCUS_UNIFORMS.uFocusSpan.value; },
};

/** GLSL declarations to inject into a fragment shader that wants the pool.
 *  Pair with FOCUS_POOL_GLSL inside main(). */
export const FOCUS_DECL = /* glsl */ `
  uniform float uFocusY;
  uniform float uFocusSpan;
  uniform float uFocusGain;
  // 1.0 at the focus height, easing to 0.0 at ±uFocusSpan away (smooth, no band)
  float focusPool(float worldY) {
    float d = abs(worldY - uFocusY) / max(uFocusSpan, 0.001);
    return 1.0 - smoothstep(0.0, 1.0, d);
  }
`;